import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import Button from './Button';
import FilingActionModal from './FilingActionModal';
import returnFilingAPI from '../api/returnFilingAPI';
import useGuardedMutation from '../hooks/useGuardedMutation';
import notify from '../lib/notify';
import { reconcile } from '../utils/reconcile';
import { ATTACHMENT_TYPES } from '../utils/recordAttachmentExport';

// BIR quarterly VAT (2550Q) due dates: 25th of the month after quarter-end.
// Q4 (Oct-Dec) falls due in the following calendar year.
const DUE_MONTH_DAY_BY_QUARTER = { 1: [4, 25], 2: [7, 25], 3: [10, 25], 4: [1, 25] };
const computeDueDate = (fiscalYear, quarter) => {
  const [month, day] = DUE_MONTH_DAY_BY_QUARTER[quarter];
  const year = quarter === 4 ? fiscalYear + 1 : fiscalYear;
  return new Date(year, month - 1, day);
};

// SLSP is tracked as two attachments (Input/Output) because the app already
// exports them as two separate files with two separate buttons in the
// Records tab — collapsing them into one "SLSP" row would claim readiness
// state the underlying export flow doesn't actually have.
const ATTACHMENT_LABELS = {
  SLSP_INPUT: 'SLSP (Input)',
  SLSP_OUTPUT: 'SLSP (Output)',
  SAWT: 'SAWT (2307 credits)',
};

const FilingCard = ({
  client,
  formCode = '2550Q',
  fiscalYear,
  quarter,
  creditsClaimed: creditsClaimedProp = 0,
  hasSlsp = true,
  onReviewAttachment,
  onGoToRecords,
}) => {
  const clientId = client?._id;
  const [filing, setFiling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // 'file' | 'unfile' | null

  const load = useCallback(async () => {
    if (!clientId || !fiscalYear || !quarter) return;
    try {
      setLoading(true);
      const res = await returnFilingAPI.get(clientId, formCode, fiscalYear, quarter);
      setFiling(res?.data || null);
    } catch (error) {
      console.error('Failed to load filing status:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, formCode, fiscalYear, quarter]);

  useEffect(() => {
    load();
  }, [load]);

  const { mutate: fileMutate, isPending: filePending } = useGuardedMutation(
    ({ filedAt, reference }) => returnFilingAPI.markFiled(clientId, formCode, fiscalYear, quarter, { filedAt, reference })
  );
  const { mutate: unfileMutate, isPending: unfilePending } = useGuardedMutation((reason) =>
    returnFilingAPI.unfile(clientId, formCode, fiscalYear, quarter, { reason })
  );
  const { mutate: validateMutate } = useGuardedMutation((type) =>
    returnFilingAPI.validateAttachment(clientId, formCode, fiscalYear, quarter, type)
  );

  const creditsClaimed = Number(creditsClaimedProp || 0);
  const showSawt = creditsClaimed > 0;

  const attachmentTypes = useMemo(() => {
    const types = hasSlsp ? [ATTACHMENT_TYPES.SLSP_INPUT, ATTACHMENT_TYPES.SLSP_OUTPUT] : [];
    if (showSawt) types.push(ATTACHMENT_TYPES.SAWT);
    return types;
  }, [hasSlsp, showSawt]);

  const getAttachment = (type) => filing?.attachments?.find((a) => a.type === type) || null;

  const getState = (type) => {
    const attachment = getAttachment(type);
    if (!attachment?.exportedAt) return { state: 'not_exported', attachment, reconciliation: null };

    if (type === ATTACHMENT_TYPES.SAWT) {
      const reconciliation = reconcile({ creditsClaimed, certificateTotal: attachment.total });
      if (!reconciliation.matched) return { state: 'mismatch', attachment, reconciliation };
    }

    return {
      state: attachment.validatedAt ? 'validated' : 'exported_unvalidated',
      attachment,
      reconciliation: null,
    };
  };

  const allAttachmentsReady = useMemo(
    () => attachmentTypes.every((type) => getState(type).state === 'validated'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachmentTypes, filing, creditsClaimed]
  );

  const handleMarkFiledClick = () => {
    if (!allAttachmentsReady) {
      notify.error(
        "Can't mark as filed",
        'Export and validate every attachment for this period first.'
      );
      return;
    }
    setModalMode('file');
  };

  const handleConfirmFile = async ({ filedAt, reference }) => {
    try {
      const res = await fileMutate({ filedAt, reference });
      setFiling(res?.data || null);
      setModalMode(null);
      notify.success('Filed', `${formCode} for Q${quarter} ${fiscalYear} marked as filed.`);
    } catch (error) {
      notify.error("Couldn't save", error?.message || error || 'Unknown error — try again.');
    }
  };

  const handleConfirmUnfile = async (reason) => {
    try {
      const res = await unfileMutate(reason);
      setFiling(res?.data || null);
      setModalMode(null);
      notify.success('Reopened', `${formCode} for Q${quarter} ${fiscalYear} is open again.`);
    } catch (error) {
      notify.error("Couldn't reopen", error?.message || error || 'Unknown error — try again.');
    }
  };

  const handleValidate = async (type) => {
    try {
      const res = await validateMutate(type);
      setFiling(res?.data || null);
      notify.success('Validated', `${ATTACHMENT_LABELS[type]} marked as validated.`);
    } catch (error) {
      notify.error("Couldn't validate", error?.message || error || 'Unknown error — try again.');
    }
  };

  if (!clientId || !fiscalYear || !quarter) return null;

  const isFiled = filing?.status === 'filed';
  const dueDate = computeDueDate(fiscalYear, quarter);

  return (
    <div className="rounded-lg border border-ink-line bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-ink">
            Q{quarter} {fiscalYear} · {formCode}
          </h3>
          <div className="mt-1">
            {isFiled ? (
              <span className="text-xs px-2 py-0.5 rounded-md bg-success-50 text-success-700">
                Filed {filing.filedAt ? format(new Date(filing.filedAt), 'MMM d, yyyy') : ''}
                {filing.reference ? ` · Ref ${filing.reference}` : ''}
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-md bg-warning-50 text-warning-700">Not filed</span>
            )}
          </div>
        </div>
        <div className="text-xs text-ink-body text-right whitespace-nowrap">
          Due {format(dueDate, 'MMM d, yyyy')}
        </div>
      </div>

      <div className="mt-4 border-l-2 border-ink-line pl-3 space-y-3">
        {loading ? (
          <div className="text-xs text-ink-body">Loading attachments…</div>
        ) : (
          attachmentTypes.map((type) => {
            const { state, attachment, reconciliation } = getState(type);

            return (
              <div key={type} className="text-sm">
                <div className="text-xs font-medium text-ink-body mb-0.5">{ATTACHMENT_LABELS[type]}</div>

                {state === 'not_exported' && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-ink-body">Not exported</span>
                    <button type="button" onClick={onGoToRecords} className="text-primary-500 font-medium text-sm">
                      Export DAT
                    </button>
                  </div>
                )}

                {state === 'exported_unvalidated' && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5 text-ink-body">
                      <AlertCircle className="w-4 h-4 text-warning-500 shrink-0" />
                      Exported {attachment.exportedAt ? format(new Date(attachment.exportedAt), 'MMM d, yyyy') : ''} · not yet
                      validated
                    </span>
                    <span className="flex gap-3 shrink-0">
                      <button type="button" onClick={() => handleValidate(type)} className="text-primary-500 font-medium text-sm">
                        Mark validated
                      </button>
                      <button type="button" onClick={onGoToRecords} className="text-primary-500 font-medium text-sm">
                        Re-export
                      </button>
                    </span>
                  </div>
                )}

                {state === 'validated' && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5 text-ink-body">
                      <CheckCircle2 className="w-4 h-4 text-success-500 shrink-0" />
                      DAT exported · validated{' '}
                      {attachment.validatedAt ? format(new Date(attachment.validatedAt), 'MMM d, yyyy') : ''}
                    </span>
                    <span className="flex gap-3 shrink-0">
                      <button type="button" onClick={onGoToRecords} className="text-primary-500 font-medium text-sm">
                        Re-export
                      </button>
                      <button type="button" onClick={() => window.print()} className="text-primary-500 font-medium text-sm">
                        Print
                      </button>
                    </span>
                  </div>
                )}

                {state === 'mismatch' && (
                  <div>
                    <div className="flex items-center gap-1.5 text-danger-500">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span className="tabular-nums">
                        Totals don't match: {ATTACHMENT_LABELS[type]} ₱{(attachment.total || 0).toFixed(2)} vs claimed ₱
                        {creditsClaimed.toFixed(2)} (Δ ₱{Math.abs(reconciliation.difference).toFixed(2)})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onReviewAttachment?.('2307')}
                      className="text-primary-500 font-medium text-sm mt-0.5"
                    >
                      Review 2307s
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="mt-4 flex justify-end">
        {isFiled ? (
          <Button variant="outline" size="sm" onClick={() => setModalMode('unfile')}>
            Unfile
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={handleMarkFiledClick}>
            Mark as filed
          </Button>
        )}
      </div>

      <FilingActionModal
        isOpen={modalMode === 'file'}
        mode="file"
        loading={filePending}
        onCancel={() => setModalMode(null)}
        onConfirm={handleConfirmFile}
      />
      <FilingActionModal
        isOpen={modalMode === 'unfile'}
        mode="unfile"
        loading={unfilePending}
        onCancel={() => setModalMode(null)}
        onConfirm={handleConfirmUnfile}
      />
    </div>
  );
};

export default FilingCard;
